import { useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import {
  canRequestQualification,
  getTrainingProgress,
  markModuleComplete,
  requestQualification,
} from '../../lib/api'
import { useAsync } from '../../lib/useAsync'
import {
  Badge,
  Card,
  ErrorState,
  Loading,
  Notice,
  PageHeader,
  ProgressBar,
  Section,
} from '../../components/ui'
import { formatDay, pluralize } from '../../lib/format'
import { useAction, useLiveReload } from './coachHooks'
import type {
  CoachServiceReadiness,
  ModuleCode,
  Qualification,
  TrainingModule,
} from '../../types/db'

const MODULE_LABEL: Record<ModuleCode, string> = {
  module_1: 'Module 1',
  module_2: 'Module 2',
  module_3: 'Module 3',
  module_4: 'Module 4',
}

/**
 * Training.
 *
 * Two lists, one rule. The modules list is what the coach works through; the
 * services list is what those modules unlock. Finishing a service's module set
 * makes the coach eligible and shows the request button; only Michel's
 * approval makes them qualified. Nothing here invents content: a module shows
 * the title, summary and body that La Folie stored for it, and nothing else.
 *
 * Copy rule: no em dashes in anything a coach reads, plain hyphens only.
 */
export function CoachTraining() {
  const { profile } = useAuth()
  const coachId = profile?.id ?? ''
  const { data, error, loading, reload } = useAsync(
    () => getTrainingProgress(coachId),
    [coachId],
  )
  useLiveReload(reload)
  const action = useAction(reload)
  const [openModule, setOpenModule] = useState<string | null>(null)

  if (loading && !data) return <Loading label="Loading your training" />
  if (error && !data) return <ErrorState message={error} onRetry={reload} />
  if (!data) return null

  const published = data.modules.filter((m) => m.is_published)
  const done = published.filter((m) => m.completion).length

  function complete(module: TrainingModule) {
    void action
      .run(() => markModuleComplete(coachId, module.id), `${module.title} marked as done.`)
      .then((ok) => {
        if (ok) setOpenModule(null)
      })
  }

  function request(row: CoachServiceReadiness) {
    void action.run(
      () => requestQualification(coachId, row.service_id),
      `Request sent. Michel will be in touch about ${row.service_name}.`,
    )
  }

  return (
    <>
      <PageHeader
        title="Training"
        subtitle="Finish the modules a service needs, then Michel approves you for it."
      />

      {action.error ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="warning">{action.error}</Notice>
        </div>
      ) : null}
      {action.done ? (
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <Notice tone="success">{action.done}</Notice>
        </div>
      ) : null}

      <Card tone="muted" className="stack">
        <div className="row row--between">
          <span className="small muted">Modules completed</span>
          <span className="small">
            {done} of {published.length}
          </span>
        </div>
        <ProgressBar value={done} max={published.length} label="Training progress" />
      </Card>

      <Section title="Modules" count={data.modules.length}>
        <div className="stack">
          {data.modules.map((module) => {
            const isOpen = openModule === module.id
            const completed = Boolean(module.completion)
            return (
              <Card key={module.id}>
                <div className="row row--between">
                  <div style={{ minWidth: 0 }}>
                    <p className="card__title">{module.title}</p>
                    <p className="small muted" style={{ marginTop: 2 }}>
                      {module.summary}
                    </p>
                  </div>
                  {completed ? (
                    <Badge tone="positive">Done</Badge>
                  ) : module.is_published ? (
                    <Badge tone="warning">To do</Badge>
                  ) : (
                    <Badge tone="neutral">Coming soon</Badge>
                  )}
                </div>

                <div className="card__foot">
                  <span className="faint">
                    {module.estimated_minutes
                      ? `About ${module.estimated_minutes} minutes`
                      : 'Length not set'}
                  </span>
                  <span className="spacer" />
                  {module.completion ? (
                    <span className="faint">
                      Completed {formatDay(module.completion.completed_at)}
                    </span>
                  ) : module.is_published ? (
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => setOpenModule(isOpen ? null : module.id)}
                    >
                      {isOpen ? 'Close' : 'Open module'}
                    </button>
                  ) : null}
                </div>

                {isOpen && !completed ? (
                  <div style={{ marginTop: 'var(--sp-4)' }}>
                    {module.content_md ? (
                      <ModuleBody markdown={module.content_md} />
                    ) : (
                      <p className="small muted">
                        The full material for this module is shared with you by Michel. Mark it as
                        done once you have been through it.
                      </p>
                    )}
                    <button
                      type="button"
                      className="btn btn--block"
                      style={{ marginTop: 'var(--sp-4)' }}
                      disabled={action.busy}
                      onClick={() => complete(module)}
                    >
                      Mark as done
                    </button>
                    <p className="faint" style={{ marginTop: 'var(--sp-2)' }}>
                      A short check-your-understanding quiz replaces this button once the module
                      content is finalised.
                    </p>
                  </div>
                ) : null}

                {!module.is_published ? (
                  <p className="faint" style={{ marginTop: 'var(--sp-3)' }}>
                    This module is still being prepared, so it cannot be finished in the app yet.
                    Michel approves the services that need it by hand for now.
                  </p>
                ) : null}
              </Card>
            )
          })}
        </div>
      </Section>

      <Section title="Services" count={data.readiness.length}>
        <div className="stack">
          {data.readiness.map((row) => (
            <ReadinessCard
              key={row.service_id}
              row={row}
              qualification={
                data.qualifications.find((q) => q.service_id === row.service_id) ?? null
              }
              modules={data.modules}
              busy={action.busy}
              onRequest={() => request(row)}
            />
          ))}
        </div>
      </Section>
    </>
  )
}

function ReadinessCard({
  row,
  qualification,
  modules,
  busy,
  onRequest,
}: {
  row: CoachServiceReadiness
  qualification: Qualification | null
  modules: TrainingModule[]
  busy: boolean
  onRequest: () => void
}) {
  const status = row.qualification_status
  // A revoked row that was approved once is a withdrawal; one that never was
  // is a decline. Same wording as the inbox notification for the decision.
  const wasApproved = qualification?.approved_at != null
  const missing = row.missing_module_codes.map(
    (code) => modules.find((m) => m.code === code) ?? null,
  )
  const missingTitles = row.missing_module_codes.map(
    (code, i) => missing[i]?.title ?? MODULE_LABEL[code],
  )
  // A service whose set includes a module nobody can finish yet. The honest
  // answer is that this one is Michel's call, not a button.
  const blockedBy = missing.filter((m): m is TrainingModule => m !== null && !m.is_published)
  const requestable = canRequestQualification(row)

  return (
    <Card>
      <div className="row row--between">
        <div style={{ minWidth: 0 }}>
          <p className="card__title">{row.service_name}</p>
          <p className="small muted" style={{ marginTop: 2 }}>
            {row.modules_completed} of {row.modules_required}{' '}
            {pluralize(row.modules_required, 'module')} done
          </p>
        </div>
        {status === 'approved' ? (
          <Badge tone="positive">Qualified</Badge>
        ) : status === 'pending' ? (
          <Badge tone="warning">Waiting for Michel</Badge>
        ) : status === 'revoked' ? (
          <Badge tone="danger">{wasApproved ? 'Approval withdrawn' : 'Not approved yet'}</Badge>
        ) : row.modules_complete ? (
          <Badge tone="info">Ready to request</Badge>
        ) : (
          <Badge tone="neutral">In training</Badge>
        )}
      </div>

      <div style={{ marginTop: 'var(--sp-3)' }}>
        <ProgressBar
          value={row.modules_completed}
          max={row.modules_required}
          label={`${row.service_name} progress`}
        />
      </div>

      {status === 'approved' ? (
        <p className="faint" style={{ marginTop: 'var(--sp-2)' }}>
          {row.qualification_approved_at
            ? `Approved ${formatDay(row.qualification_approved_at)}. These events show in your schedule.`
            : 'Approved. These events show in your schedule.'}
        </p>
      ) : null}

      {status === 'revoked' && qualification?.revoke_reason ? (
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <Notice tone="warning">
            Reason given: {qualification.revoke_reason}{' '}
            {wasApproved
              ? 'Events for this service leave your schedule for now. Talk to Michel about what would bring it back.'
              : 'You can ask again once this is sorted.'}
          </Notice>
        </div>
      ) : null}

      {status !== 'approved' && missingTitles.length > 0 ? (
        <p className="faint" style={{ marginTop: 'var(--sp-2)' }}>
          Still needed: {missingTitles.join(', ')}
        </p>
      ) : null}

      {status !== 'approved' && blockedBy.length > 0 ? (
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <Notice tone="info">
            {blockedBy.map((m) => m.title).join(' and ')}{' '}
            {blockedBy.length === 1 ? 'is' : 'are'} not published yet, so {row.service_name} cannot
            be finished in the app. Approval for it stays a manual decision by Michel until then:
            talk to him directly if you want to be considered.
          </Notice>
        </div>
      ) : null}

      {requestable ? (
        <button
          type="button"
          className="btn btn--block"
          style={{ marginTop: 'var(--sp-3)' }}
          disabled={busy}
          onClick={onRequest}
        >
          Request qualification
        </button>
      ) : null}

      {status === 'pending' ? (
        <div style={{ marginTop: 'var(--sp-3)' }} className="stack stack--tight">
          <button type="button" className="btn btn--block btn--secondary" disabled>
            Request pending
          </button>
          <Notice tone="info">
            Michel does a short call before approving. He will message you on WhatsApp to arrange
            it.
          </Notice>
        </div>
      ) : null}
    </Card>
  )
}

/**
 * Deliberately tiny markdown renderer: headings, paragraphs, nothing else.
 * Module content is authored by La Folie and stored in the database, so a full
 * markdown library plus sanitiser is not worth the bytes at this stage. If
 * richer content is needed later, swap this for a real renderer and sanitise.
 */
function ModuleBody({ markdown }: { markdown: string }) {
  const blocks = markdown.split('\n').filter((line) => line.trim().length > 0)
  return (
    <div className="module-body">
      {blocks.map((line, i) =>
        line.startsWith('## ') ? (
          <h4 key={i}>{line.slice(3)}</h4>
        ) : (
          <p key={i}>{line}</p>
        ),
      )}
    </div>
  )
}
